
import { useCallback, useRef } from 'react';

export function useSound() {
  const audioContextRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playSpinSound = useCallback(() => {
    const ctx = getAudioContext();
    const duration = 4.2;
    const startTime = ctx.currentTime;

    // Cute xylophone-like ticks that slow down
    const tickCount = 50;
    const cuteNotes = [1047, 1175, 1319, 1397, 1568, 1760, 1976, 2093]; // C6-C7 pentatonic-ish

    for (let i = 0; i < tickCount; i++) {
      const progress = i / tickCount;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const tickTime = startTime + easeOut * duration;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(ctx.destination);

      // Cycle through cute high-pitched notes
      osc.frequency.value = cuteNotes[i % cuteNotes.length];
      osc.type = 'triangle'; // Softer, bell-like tone

      const vol = 0.12 * (1 - progress * 0.6);
      gain.gain.setValueAtTime(vol, tickTime);
      gain.gain.linearRampToValueAtTime(0.001, tickTime + 0.08);

      osc.start(tickTime);
      osc.stop(tickTime + 0.08);

      // Add a subtle harmonic overtone for sparkle
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = cuteNotes[i % cuteNotes.length] * 2;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(vol * 0.3, tickTime);
      gain2.gain.linearRampToValueAtTime(0.001, tickTime + 0.06);
      osc2.start(tickTime);
      osc2.stop(tickTime + 0.06);
    }
  }, [getAudioContext]);

  const playWinSound = useCallback(() => {
    const ctx = getAudioContext();
    const startTime = ctx.currentTime;

    // Cute bubbly ascending melody
    const melody = [
      { freq: 1047, time: 0, dur: 0.12 },    // C6
      { freq: 1319, time: 0.1, dur: 0.12 },   // E6
      { freq: 1568, time: 0.2, dur: 0.12 },   // G6
      { freq: 2093, time: 0.32, dur: 0.25 },   // C7 (hold)
      { freq: 1760, time: 0.5, dur: 0.1 },    // A6
      { freq: 2093, time: 0.6, dur: 0.35 },   // C7 (final hold)
    ];

    melody.forEach(({ freq, time, dur }) => {
      // Main tone - triangle for cute bell sound
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'triangle';

      const noteStart = startTime + time;
      gain.gain.setValueAtTime(0, noteStart);
      gain.gain.linearRampToValueAtTime(0.2, noteStart + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, noteStart + dur);

      osc.start(noteStart);
      osc.stop(noteStart + dur);

      // Sparkle overtone
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = freq * 2;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0, noteStart);
      gain2.gain.linearRampToValueAtTime(0.06, noteStart + 0.02);
      gain2.gain.exponentialRampToValueAtTime(0.001, noteStart + dur * 0.7);
      osc2.start(noteStart);
      osc2.stop(noteStart + dur);
    });

    // Cute sparkle/twinkle effect at the end
    for (let i = 0; i < 12; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.frequency.value = 2500 + Math.random() * 2500;
      osc.type = 'sine';

      const sparkleStart = startTime + 0.55 + i * 0.04;
      gain.gain.setValueAtTime(0.04, sparkleStart);
      gain.gain.exponentialRampToValueAtTime(0.001, sparkleStart + 0.1);

      osc.start(sparkleStart);
      osc.stop(sparkleStart + 0.1);
    }

    // Soft "pop" at the very start for extra cuteness
    const pop = ctx.createOscillator();
    const popGain = ctx.createGain();
    pop.connect(popGain);
    popGain.connect(ctx.destination);
    pop.frequency.setValueAtTime(600, startTime);
    pop.frequency.exponentialRampToValueAtTime(150, startTime + 0.08);
    pop.type = 'sine';
    popGain.gain.setValueAtTime(0.15, startTime);
    popGain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.08);
    pop.start(startTime);
    pop.stop(startTime + 0.08);
  }, [getAudioContext]);

  return { playSpinSound, playWinSound };
}
